import { mount, StartClient } from '@solidjs/start/client';
import { AnalyticsProvider } from '@troon/analytics';
import { createClient } from '@troon/analytics/client';
import { getUserAgent, isPerformanceRestrictedUserAgent } from '@troon/user-agent';
import { createApiClient } from '@troon/api-client';
import { getCurrentScope } from '@sentry/solidstart';
import { sampleByEvent } from 'posthog-js/lib/src/customizations';
import { ErrorBoundary } from './components/error-boundary';
import { getConfigValue } from './modules/config';
import { initSentry } from './modules/sentry/browser';
import { polyfill } from './providers/polyfill';

// Polyfill for Object.hasOwn – we get hit by lots of old bots that need this
if (!Object.hasOwn) {
	Object.defineProperty(Object, 'hasOwn', {
		value: function (obj: Record<string, unknown>, prop: string) {
			return Object.prototype.hasOwnProperty.call(obj, prop);
		},
		configurable: true,
		enumerable: false,
		writable: true,
	});
}

window.addEventListener('vite:preloadError', () => {
	window.location.reload(); // for example, refresh the page
});

const apiClient = createApiClient({ baseUrl: getConfigValue('API_URL') });
apiClient.use({
	async onRequest({ request }) {
		const headers: Record<string, string | undefined> = {
			'x-troon-client-version': window.__CTX__.sha,
			'x-session-id': window.__ID__.sess,
		};
		try {
			headers['x-trace-id'] = window.crypto.randomUUID();
		} catch {
			// missing randomUUID is only in unsupported browsers
		}

		for (const [key, val] of Object.entries(headers)) {
			if (val) {
				request.headers.set(key, val);
			}
		}

		const scope = getCurrentScope();
		scope.setExtras(headers);
		return request;
	},
});

async function startup() {
	await polyfill();
	const ua = getUserAgent();
	const isDisallowedClient = isPerformanceRestrictedUserAgent(ua);

	const posthog = createClient({
		key: getConfigValue('POSTHOG_WRITE_KEY'),
		api_host: `https://${getConfigValue('HOST')}/_/ingest`,
		bootstrap: {
			// If not logged in, use session ID & ensure isIdentifiedID is false
			distinctID: window.__USER__?.id ?? (window.__ID__.dist || window.__ID__.sess) ?? undefined,
			isIdentifiedID: !!window.__USER__?.id,
			featureFlags: window.__FLAGS__,
			featureFlagPayloads: window.__FLAG_PAYLOADS__,
		},
		disable_session_recording: getConfigValue('POSTHOG_ENABLE_SESSION_RECORDINGS'),
		capture_dead_clicks: false,
		before_send: [
			sampleByEvent(['$web_vitals'], 0.5),
			(result) => {
				if (!result) {
					return null;
				}

				// Rare use-case to actually use import.meta.env
				// This code will be fully dropped from staging and production builds
				if (import.meta.env.ACCESS_ENVIRONMENT === 'development') {
					const props = Object.entries(result.properties ?? {}).reduce(
						(memo, [key, val]) => {
							const ignores = ['distinct_id', 'token'];
							if (key.startsWith('$') || ignores.includes(key)) {
								return memo;
							}
							memo[key] = val;
							return memo;
						},
						{} as Record<string, unknown>,
					);
					/* eslint-disable no-console */
					console.groupCollapsed('%cEvent', 'background-color:#02666d;color:white;padding:0 0.25rem;', result?.event);
					console.info(props);
					console.info(result);
					console.groupEnd();
					/* eslint-enable no-console */
				}

				if (!getConfigValue('POSTHOG_ENABLED') || isDisallowedClient) {
					return null;
				}
				return result;
			},
		],
	});

	if (!isDisallowedClient && getConfigValue('SENTRY_ENABLED')) {
		requestIdleCallback(() => {
			initSentry(posthog);
		});
	}

	mount(() => {
		return (
			<ErrorBoundary>
				<AnalyticsProvider
					gtmId={isDisallowedClient || window.__CTX__?.performanceRestricted ? undefined : getConfigValue('PIXEL_GTM')}
					cookieYesId={
						posthog?.isFeatureEnabled('cookie-banner') || window.__FLAGS__?.['cookie-banner']
							? getConfigValue('COOKIE_YES_ID')
							: undefined
					}
					posthog={posthog}
					flags={window.__FLAGS__}
				>
					<StartClient />
				</AnalyticsProvider>
			</ErrorBoundary>
		);
	}, document.getElementById('app')!);
}

startup();
